import type { CognitoTokens } from './oauth';

const TOKEN_STORAGE_KEY = 'budget.cloud.tokens.v1';
const EXPIRY_SKEW_MS = 30_000;

type StorageLike = Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>;

export function loadCloudTokens(
  storage: StorageLike = localStorage,
): CognitoTokens | null {
  const raw = storage.getItem(TOKEN_STORAGE_KEY);
  if (raw === null) return null;

  try {
    const value: unknown = JSON.parse(raw);
    if (!isRecord(value)) return null;
    if (
      typeof value.accessToken !== 'string' ||
      value.accessToken === '' ||
      typeof value.expiresAt !== 'number' ||
      !Number.isFinite(value.expiresAt) ||
      !isNullableString(value.idToken) ||
      !isNullableString(value.refreshToken)
    ) {
      return null;
    }
    return {
      accessToken: value.accessToken,
      expiresAt: value.expiresAt,
      idToken: value.idToken,
      refreshToken: value.refreshToken,
    };
  } catch {
    return null;
  }
}

export function saveCloudTokens(
  tokens: CognitoTokens,
  storage: StorageLike = localStorage,
): void {
  storage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
}

export function clearCloudTokens(storage: StorageLike = localStorage): void {
  storage.removeItem(TOKEN_STORAGE_KEY);
}

export function isAccessTokenUsable(
  tokens: CognitoTokens,
  now: number = Date.now(),
): boolean {
  return tokens.expiresAt - EXPIRY_SKEW_MS > now;
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
